/**
 * File / Image input.
 *
 * Two modes:
 *   - With `uploadCtx` → real upload to Supabase storage (data-entry / review).
 *   - Without `uploadCtx` → preview placeholder; no upload happens. Used by
 *     the form-builder preview pane.
 *
 * Pre-upload checks come from the validation engine
 * (`checkFileAgainstRules`); the `accept` attribute is derived via
 * `acceptAttrFromRules`.
 */
import { useEffect, useState } from 'react'
import { AlertCircle, CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  acceptAttrFromRules,
  checkFileAgainstRules,
  imageDimensionsProbe,
} from '@/lib/form-engine'
import {
  uploadFileToStorage,
  getSignedUrl,
  type UploadedFileRef,
} from '@/features/records/api/file-upload-api'
import type { InputProps } from '../input-props'

function isUploadedFileRef(v: unknown): v is UploadedFileRef {
  return !!v && typeof v === 'object' && 'storagePath' in v && 'bucket' in v
}

export function FileInput(props: InputProps) {
  return <FileLike {...props} kind="file" />
}

export function ImageInput(props: InputProps) {
  return <FileLike {...props} kind="image" />
}

function FileLike({
  field, value, onChange, onBlur, disabled, uploadCtx,
  kind,
}: InputProps & { kind: 'file' | 'image' }) {
  const validations = field.validations ?? []
  const accept = kind === 'image' ? 'image/*' : acceptAttrFromRules(validations)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [signedUrl, setSignedUrl] = useState<string | null>(null)

  const currentRef = isUploadedFileRef(value) ? value : null

  useEffect(() => {
    if (!currentRef || kind !== 'image' || !currentRef.mimeType.startsWith('image/')) {
      setSignedUrl(null)
      return
    }
    let cancelled = false
    getSignedUrl(currentRef.bucket, currentRef.storagePath).then((url) => {
      if (!cancelled) setSignedUrl(url)
    })
    return () => { cancelled = true }
  }, [currentRef?.storagePath, currentRef?.bucket, currentRef?.mimeType, kind])

  // Preview-only mode (no upload context provided).
  if (!uploadCtx) {
    return (
      <div className="bg-muted/30 border border-dashed border-border rounded-md p-3 text-xs text-muted-foreground text-center">
        فایل در حالت پیش‌نمایش بارگذاری نمی‌شود
      </div>
    )
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadError(null)

    const check = await checkFileAgainstRules(file, validations, imageDimensionsProbe)
    if (!check.ok) {
      setUploadError(check.message ?? 'فایل معتبر نیست')
      e.target.value = ''
      return
    }

    setUploading(true)
    try {
      const ref = uploadCtx.upload
        ? await uploadCtx.upload(file, field.field_key)
        : await uploadFileToStorage(
            file,
            uploadCtx.projectId,
            uploadCtx.recordId,
            uploadCtx.formId,
            field.field_key,
            uploadCtx.submissionId,
          )
      onChange(ref)
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'خطا در آپلود فایل')
    } finally {
      setUploading(false)
    }
  }

  const handleDownload = async () => {
    if (!currentRef) return
    const url = await getSignedUrl(currentRef.bucket, currentRef.storagePath)
    if (url) window.open(url, '_blank')
  }

  return (
    <div className="space-y-2" onBlur={onBlur}>
      {currentRef && kind === 'image' && signedUrl && currentRef.mimeType.startsWith('image/') && (
        <img
          src={signedUrl}
          alt={currentRef.originalName}
          className="max-h-48 rounded-md border border-border object-contain"
        />
      )}
      {currentRef && (
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <CheckCircle2 className="w-3 h-3 text-emerald-400" />
            {currentRef.originalName}
          </span>
          <Button type="button" variant="outline" size="sm" className="h-6 px-2 text-xs" onClick={handleDownload}>
            دانلود
          </Button>
        </div>
      )}
      <input
        type="file"
        accept={kind === 'image' ? 'image/*' : accept}
        disabled={disabled || uploading}
        className="text-sm text-foreground file:me-3 file:py-1.5 file:px-3 file:rounded-md file:border file:border-border file:bg-card file:text-sm file:cursor-pointer disabled:opacity-50"
        onChange={handleFileChange}
      />
      {uploading && <p className="text-xs text-muted-foreground">در حال آپلود...</p>}
      {uploadError && (
        <p className="text-xs text-destructive flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          {uploadError}
        </p>
      )}
    </div>
  )
}
